import { useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import { bouncy } from "ldrs";
import { useSlaUserMatrixStore } from "./store/useSlaUserMatrixStore";

bouncy.register();

const cancelButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #1e40af",
  background: "#ffffff",
  color: "#1e40af",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
};

const saveButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 72,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const sanitizeMinuteValue = (value) => {
  const numericOnly = String(value).replace(/\D/g, "").slice(0, 5);
  if (!numericOnly) return "";
  const parsed = parseInt(numericOnly, 10);
  if (parsed > 99999) return "99999";
  return String(parsed);
};

const blockNonNumericKeys = (event) => {
  if (["e", "E", "+", "-", ".", ","].includes(event.key)) {
    event.preventDefault();
  }
};

const AddEditSlaUserMatrixForm = ({
  showModal,
  setShowModal,
  editRow,
  isEditMode,
}) => {
  const { userOptions, levelOptions, isSaving, saveSlaUserMatrix, fetchMasterOptions } =
    useSlaUserMatrixStore();

  useEffect(() => {
    if (!showModal) return;
    fetchMasterOptions();
  }, [showModal, fetchMasterOptions]);

  const handleClose = () => {
    setShowModal(false);
  };

  const resolveLevelId = () => {
    if (editRow?.levelId) return editRow.levelId.toString();
    const matchedLevel = levelOptions.find(
      (level) =>
        level.text === editRow?.level ||
        level.text === editRow?.levelName ||
        String(level.value) === String(editRow?.level)
    );
    return matchedLevel ? String(matchedLevel.value) : "";
  };

  const resolveUserId = () => {
    if (editRow?.userId) return editRow.userId.toString();
    const matchedUser = userOptions.find(
      (user) => user.userName?.trim() === editRow?.userName?.trim()
    );
    return matchedUser ? String(matchedUser.userId) : "";
  };

  const initialValues = {
    userId: isEditMode ? resolveUserId() : "",
    levelId: isEditMode ? resolveLevelId() : "",
    alertBeforeEscalationMin: isEditMode
      ? editRow?.alertBeforeEscalationMin?.toString() || ""
      : "",
    escalationDurationMin: isEditMode
      ? editRow?.escalationDurationMin?.toString() || ""
      : "",
    isActive: isEditMode
      ? editRow?.isActive === false
        ? "false"
        : "true"
      : "true",
  };

  const validationSchema = Yup.object().shape({
    userId: Yup.string().required("User is required"),
    levelId: Yup.string().required("Level is required"),
    alertBeforeEscalationMin: Yup.number()
      .typeError("SLA Alert Before Esclation is required")
      .required("SLA Alert Before Esclation is required")
      .min(1, "Value must be between 1 and 99999")
      .max(99999, "Value must be between 1 and 99999")
      .integer("Must be a whole number"),
    escalationDurationMin: Yup.number()
      .typeError("SLA Esclation Duration is required")
      .required("SLA Esclation Duration is required")
      .min(1, "Value must be between 1 and 99999")
      .max(99999, "Value must be between 1 and 99999")
      .integer("Must be a whole number")
      .test(
        "greater-than-alert",
        "Escalation duration must be greater than alert duration",
        function (value) {
          const { alertBeforeEscalationMin } = this.parent;
          if (!value || !alertBeforeEscalationMin) return true;
          return Number(value) > Number(alertBeforeEscalationMin);
        }
      ),
    isActive: Yup.string().required("Status is required"),
  });

  const handleSubmit = async (values) => {
    const payload = {
      userId: values.userId,
      levelId: values.levelId,
      alertBeforeEscalationMin: values.alertBeforeEscalationMin,
      escalationDurationMin: values.escalationDurationMin,
      isActive: values.isActive === "true",
    };

    if (isEditMode) {
      payload.tMatrixId = editRow?.tMatrixId ?? editRow?.slaUserMatrixId;
    }

    const result = await saveSlaUserMatrix(payload, isEditMode);
    if (result?.success) {
      handleClose();
    }
  };

  return (
    <Modal
      size="md"
      show={showModal}
      onHide={handleClose}
      aria-labelledby="sla-user-matrix-modal-title"
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title id="sla-user-matrix-modal-title">
          {isEditMode ? "Edit SLA User Matrix" : "Add SLA User Matrix"}
        </Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              handleClose();
            }
          }}
          aria-label="Close modal"
        ></i>
      </Modal.Header>
      <hr className="mt-4" />
      <Modal.Body>
        <Formik
          key={
            isEditMode
              ? `${editRow?.slaUserMatrixId}-${levelOptions.length}-${userOptions.length}`
              : "new"
          }
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="mb-3">
                <label htmlFor="userId" className="form-label">
                  User
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Select
                  inputId="userId"
                  name="userId"
                  classNamePrefix="react-select"
                  options={userOptions.map((user) => ({
                    value: String(user.userId),
                    label: user.employeeCode
                      ? `${user.userName} (${user.employeeCode})`
                      : user.userName,
                  }))}
                  value={
                    userOptions
                      .map((user) => ({
                        value: String(user.userId),
                        label: user.employeeCode
                          ? `${user.userName} (${user.employeeCode})`
                          : user.userName,
                      }))
                      .find((option) => option.value === String(values.userId)) ||
                    null
                  }
                  onChange={(selected) =>
                    setFieldValue("userId", selected ? selected.value : "")
                  }
                  placeholder="Select User"
                  isClearable={false}
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                <ErrorMessage
                  className="required-message"
                  name="userId"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="levelId" className="form-label">
                  Level
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Select
                  inputId="levelId"
                  name="levelId"
                  classNamePrefix="react-select"
                  options={levelOptions.map((level) => ({
                    value: String(level.value),
                    label: level.text,
                  }))}
                  value={
                    levelOptions
                      .map((level) => ({
                        value: String(level.value),
                        label: level.text,
                      }))
                      .find((option) => option.value === String(values.levelId)) ||
                    null
                  }
                  onChange={(selected) =>
                    setFieldValue("levelId", selected ? selected.value : "")
                  }
                  placeholder="Select Level"
                  isClearable={false}
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                <ErrorMessage
                  className="required-message"
                  name="levelId"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="alertBeforeEscalationMin" className="form-label">
                  SLA Alert Before Esclation(In Min)
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Field
                  type="number"
                  name="alertBeforeEscalationMin"
                  id="alertBeforeEscalationMin"
                  className="form-control"
                  placeholder="Enter Minutes"
                  min={1}
                  max={99999}
                  step={1}
                  onKeyDown={blockNonNumericKeys}
                  onChange={(event) => {
                    setFieldValue(
                      "alertBeforeEscalationMin",
                      sanitizeMinuteValue(event.target.value)
                    );
                  }}
                />
                <ErrorMessage
                  className="required-message"
                  name="alertBeforeEscalationMin"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="escalationDurationMin" className="form-label">
                  SLA Esclation Duration(In Min)
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Field
                  type="number"
                  name="escalationDurationMin"
                  id="escalationDurationMin"
                  className="form-control"
                  placeholder="Enter Minutes"
                  min={1}
                  max={99999}
                  step={1}
                  onKeyDown={blockNonNumericKeys}
                  onChange={(event) => {
                    setFieldValue(
                      "escalationDurationMin",
                      sanitizeMinuteValue(event.target.value)
                    );
                  }}
                />
                <ErrorMessage
                  className="required-message"
                  name="escalationDurationMin"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Status
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <div className="d-flex align-items-center gap-4 mt-1">
                  <div className="form-check mb-0">
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="slaMatrixActive"
                      value="true"
                    />
                    <label className="form-check-label" htmlFor="slaMatrixActive">
                      Active
                    </label>
                  </div>
                  <div className="form-check mb-0">
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="slaMatrixInactive"
                      value="false"
                    />
                    <label className="form-check-label" htmlFor="slaMatrixInactive">
                      Inactive
                    </label>
                  </div>
                </div>
                <ErrorMessage
                  className="required-message"
                  name="isActive"
                  component="div"
                />
              </div>

              <hr />
              <div className="d-flex justify-content-end gap-2">
                <button type="button" style={cancelButtonStyle} onClick={handleClose}>
                  Cancel
                </button>
                <button type="submit" style={saveButtonStyle} disabled={isSaving}>
                  {isSaving ? (
                    <l-bouncy size="22" speed="1.75" color="white"></l-bouncy>
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditSlaUserMatrixForm;
